import { Link, useNavigate, useParams } from 'react-router-dom';
import { EmployeeType } from '@prisma/client';
import { Breadcrumb, Button, Descriptions, Form, Input, Layout, Result, Space, Spin } from 'antd';
import Select, { DefaultOptionType } from 'antd/es/select';

import { Hooks, PageLayout, Picker, Tables } from '@app/web-components';

import { useEditAccessRule, useGetAccessRule } from '../data/access-rule';
import { exceptionUniquenessValidator, FormException } from '../libs';
import { useCanEditAccessRule } from '../libs/use-can-edit-access-rule';

const PAGE_TITLE = 'Edit Access Rule';

interface AccessRuleEditFormData {
  owners: DefaultOptionType[];
  exceptions: FormException[];
}

export const AccessRuleEdit = () => {
  const [form] = Form.useForm<AccessRuleEditFormData>();

  const { id } = useParams();
  const accessRuleId = Number(id) | 0;
  const formLayoutProps = Hooks.useFormLayout();

  const { data, isFetching } = useGetAccessRule(accessRuleId, { enabled: !!accessRuleId });
  const edit = useEditAccessRule(accessRuleId);
  const { canEditAccessRule } = useCanEditAccessRule();

  const { getSelectProps } = Picker.useWorkerPicker({ mode: 'multiple' });
  const { getSelectProps: getExceptionUsersSelectProps } = Picker.useWorkerPicker({});
  const navigate = useNavigate();

  if (isFetching || !data) {
    return <Spin />;
  }

  if (!canEditAccessRule(data)) {
    navigate('/access-forbidden');
  }

  const initialValues: AccessRuleEditFormData = {
    owners: data.owners.map((owner) => ({
      id: owner.id,
      label: owner.user.name,
      value: owner.user.enterpriseId,
    })),
    exceptions: data.exceptions.map((exception) => ({
      user: {
        id: exception.id,
        label: exception.user.name,
        value: exception.user.enterpriseId,
      },
      justification: exception.justification,
    })),
  };

  const onFinish = (formData: AccessRuleEditFormData) => {
    edit.mutate({
      employeeType: data.employeeType,
      employeeSubtype: data.employeeSubtype ?? undefined,
      owners: formData.owners.map((owner) => owner.value as string),
      exceptions: formData.exceptions.flatMap((exception) => ({
        wwid: exception.user.value as string,
        justification: exception.justification,
      })),
    });
  };

  if (edit.isSuccess) {
    return (
      <Result
        status="success"
        title="Successfully updated an access rule!"
        extra={[
          <Button type="primary" key={`go to access conflicts`} onClick={() => navigate('../')}>
            Go To Access Rules
          </Button>,
        ]}
      />
    );
  }

  if (edit.isError) {
    return (
      <Result
        status="error"
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        title={(edit.error as any).response?.data?.message ?? (edit.error as any).message}
        extra={[
          <Button type="primary" key="go to access" onClick={() => navigate('../')}>
            Go To Manage Rules
          </Button>,
          <Button
            key="create another"
            onClick={() => {
              edit.reset();
            }}
          >
            Return to Edit
          </Button>,
        ]}
      />
    );
  }

  return (
    <>
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[
          { title: <Link to="/">Home</Link> },
          { title: <Link to="../">Access Rules</Link> },
          { title: PAGE_TITLE },
        ]}
      />
      <Layout.Content {...formLayoutProps}>
        <PageLayout.Title title={PAGE_TITLE} />
        <Descriptions title="Access Rule Details">
          <Descriptions.Item label="Access">{data.access.name}</Descriptions.Item>
          {data.conflictAccess ? (
            <Descriptions.Item label="Conflict Access">{data.conflictAccess.name}</Descriptions.Item>
          ) : (
            <Descriptions.Item label="Department">{data.department?.name}</Descriptions.Item>
          )}
          <Descriptions.Item label="Employee Type">{data.employeeType}</Descriptions.Item>
          {data.employeeType === EmployeeType.CONTINGENT_WORKER && (
            <Descriptions.Item label="Employee Subtype">{data.employeeSubtype}</Descriptions.Item>
          )}
        </Descriptions>
        <Form
          form={form}
          initialValues={initialValues}
          layout="vertical"
          style={{ padding: '1em' }}
          onFinish={onFinish}
        >
          <Form.Item label="Owners" name="owners" rules={[{ required: true }]}>
            <Select {...getSelectProps({ placeholder: 'Search Intel workers to set as owners' })} />
          </Form.Item>
          <Form.Item
            label="Exceptions"
            name="exceptions"
            rules={[
              {
                validator: exceptionUniquenessValidator,
              },
            ]}
          >
            <Tables.DataTable recordType="Exception">
              <Form.Item label="User" name="user" rules={[{ required: true }]}>
                <Select {...getExceptionUsersSelectProps({ style: { minWidth: '275px' } })} />
              </Form.Item>
              <Form.Item label="Justification" name="justification" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Tables.DataTable>
          </Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={edit.isPending}>
              Save
            </Button>
            <Button onClick={() => form.resetFields()}>Reset</Button>
            <Button onClick={() => navigate(-1)}>Cancel</Button>
          </Space>
        </Form>
      </Layout.Content>
    </>
  );
};
